import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { jwtDecode } from "jwt-decode";

const COGNITO_CLIENT_ID = "7883r6l59fuadm2gkgtr4tihve";
const COGNITO_REGION = "us-east-1";

const RequireAuth = ({ children }) => {
  const location = useLocation();
  const [authStatus, setAuthStatus] = useState("checking");

  useEffect(() => {
    let cancelled = false;

    const fullDestination =
      `${location.pathname || ""}${location.search || ""}${location.hash || ""}`;

    const savePendingDestination = () => {
      if (fullDestination && fullDestination !== "/login") {
        localStorage.setItem("pendingLoginDestination", fullDestination);
      }
    };

    const clearAuthValues = () => {
      localStorage.removeItem("idToken");
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userId");
      localStorage.removeItem("firstName");
      localStorage.removeItem("lastName");
      localStorage.removeItem("email");
      localStorage.removeItem("phone");
    };

    const tokenIsStillValid = (token) => {
      try {
        const decoded = jwtDecode(token);
        const expiresAtMs = Number(decoded.exp || 0) * 1000;

        // Refresh a little early so a token does not expire during
        // the next API request.
        return expiresAtMs > Date.now() + 60_000;
      } catch (err) {
        return false;
      }
    };

    const checkOrRefreshSession = async () => {
      const idToken = localStorage.getItem("idToken");

      if (idToken && tokenIsStillValid(idToken)) {
        if (!cancelled) {
          setAuthStatus("authenticated");
        }
        return;
      }

      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        savePendingDestination();
        clearAuthValues();

        if (!cancelled) {
          setAuthStatus("unauthenticated");
        }
        return;
      }

      try {
        const client = new CognitoIdentityProviderClient({
          region: COGNITO_REGION,
        });

        const response = await client.send(
          new InitiateAuthCommand({
            AuthFlow: "REFRESH_TOKEN_AUTH",
            ClientId: COGNITO_CLIENT_ID,
            AuthParameters: {
              REFRESH_TOKEN: refreshToken,
            },
          })
        );

        const newIdToken = response.AuthenticationResult?.IdToken;
        const newAccessToken = response.AuthenticationResult?.AccessToken;

        if (!newIdToken || !newAccessToken) {
          throw new Error("Cognito refresh did not return new tokens.");
        }

        localStorage.setItem("idToken", newIdToken);
        localStorage.setItem("authToken", newAccessToken);

        // Cognito normally does not return a new refresh token during
        // REFRESH_TOKEN_AUTH, so keep the existing refreshToken.
        if (!cancelled) {
          setAuthStatus("authenticated");
        }
      } catch (err) {
        console.error("Session refresh failed:", err);

        savePendingDestination();
        clearAuthValues();

        if (!cancelled) {
          setAuthStatus("unauthenticated");
        }
      }
    };

    checkOrRefreshSession();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, location.hash]);

  if (authStatus === "checking") {
    return null;
  }

  if (authStatus !== "authenticated") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

export default RequireAuth;